<template>
  <div class="tag-select">
    <el-select
      v-model="selectedTags"
      multiple
      filterable
      remote
      :remote-method="handleSearch"
      :loading="loading"
      placeholder="请选择标签"
      @visible-change="handleVisibleChange"
      class="tag-select-input"
    >
      <div class="select-header" v-if="filteredTags.length === 0">
        <el-button 
          type="primary" 
          link 
          @click.stop="showAddTagDialog"
        >
          <el-icon><Plus /></el-icon> 创建新标签
        </el-button>
      </div>

      <el-option
        v-for="tag in filteredTags"
        :key="tag.id"
        :label="tag.name"
        :value="tag.id"
      >
        <div class="tag-option">
          <el-tag
            :style="{ backgroundColor: tag.color + '20', color: tag.color }"
            effect="plain"
          >
            {{ tag.name }}
          </el-tag>
          <span class="tag-category">{{ tag.category }}</span>
        </div>
      </el-option>
    </el-select>

    <!-- 添加标签对话框 -->
    <el-dialog
      v-model="dialogVisible"
      title="添加标签"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="80px"
      >
        <el-form-item label="标签名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入标签名称" />
        </el-form-item>

        <el-form-item label="标签颜色" prop="color">
          <el-color-picker v-model="form.color" />
        </el-form-item>

        <el-form-item label="标签分类" prop="category">
          <el-select v-model="form.category" placeholder="请选择标签分类">
            <el-option
              v-for="category in TAG_CATEGORIES"
              :key="category.value"
              :label="category.label"
              :value="category.value"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { ElMessage, FormInstance } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';
import type { Tag } from '@/types/tag';
import { TAG_CATEGORIES } from '@/types/tag';
import tagApi from '@/services/tagApi';

const props = defineProps<{
  modelValue: number[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void;
}>();

// 状态变量
const loading = ref(false);
const tags = ref<Tag[]>([]);
const filteredTags = ref<Tag[]>([]);
const dialogVisible = ref(false);
const formRef = ref<FormInstance>();

// 表单数据
const form = ref({
  name: '',
  color: '#409EFF',
  category: ''
});

// 表单校验规则
const rules = {
  name: [{ required: true, message: '请输入标签名称' }],
  color: [{ required: true, message: '请选择标签颜色' }],
  category: [{ required: true, message: '请选择标签分类' }]
};

// 选中的标签
const selectedTags = ref<number[]>([]);

// 监听选中标签变化
watch(selectedTags, (newValue) => {
  emit('update:modelValue', newValue);
});

// 监听外部值变化
watch(() => props.modelValue, (newValue) => {
  selectedTags.value = newValue;
});

// 加载标签列表
const loadTags = async () => {
  try {
    loading.value = true;
    const response = await tagApi.getTags();
    tags.value = response.content;
    filteredTags.value = response.content;
  } catch (error) {
    console.error('加载标签列表失败:', error);
    ElMessage.error('加载标签列表失败');
  } finally {
    loading.value = false;
  }
};

// 搜索标签
const handleSearch = async (query: string) => {
  if (!query) {
    filteredTags.value = tags.value;
    return;
  }

  try {
    loading.value = true;
    const response = await tagApi.searchTags(query);
    filteredTags.value = response.content;
  } catch (error) {
    console.error('搜索标签失败:', error);
  } finally {
    loading.value = false;
  }
};

// 处理下拉框显示状态变化
const handleVisibleChange = (visible: boolean) => {
  if (visible) {
    loadTags();
  }
};

// 显示添加标签对话框
const showAddTagDialog = () => {
  form.value = {
    name: '',
    color: '#409EFF',
    category: ''
  };
  dialogVisible.value = true;
};

// 提交添加标签表单
const handleSubmit = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const newTag = await tagApi.addTag(form.value);
        tags.value.push(newTag);
        filteredTags.value = tags.value;
        selectedTags.value = [...selectedTags.value, newTag.id];
        dialogVisible.value = false;
        ElMessage.success('标签添加成功');
      } catch (error) {
        console.error('添加标签失败:', error);
        ElMessage.error('添加标签失败');
      }
    }
  });
};

// 初始化
onMounted(() => {
  selectedTags.value = props.modelValue;
});
</script>

<style scoped>
.tag-select {
  width: 100%;
}

.tag-select-input {
  width: 100%;
}

.select-header {
  padding: 8px;
  text-align: center;
  border-bottom: 1px solid #eee;
}

.tag-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tag-category {
  color: #909399;
  font-size: 12px;
}

:deep(.el-select-dropdown__item) {
  padding: 0 12px;
}
</style> 